import * as pdfjsLib from 'pdfjs-dist'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl

export type PdfDocument = pdfjsLib.PDFDocumentProxy

export class PasswordRequiredError extends Error {
  constructor() {
    super('This PDF is password protected.')
    this.name = 'PasswordRequiredError'
  }
}

export class WrongPasswordError extends Error {
  constructor() {
    super('Incorrect password. Please try again.')
    this.name = 'WrongPasswordError'
  }
}

export class InvalidPdfError extends Error {
  constructor() {
    super('We couldn\u2019t read this PDF. The file may be damaged or unsupported.')
    this.name = 'InvalidPdfError'
  }
}

/**
 * Reads a PDF File into a PDFDocumentProxy using PDF.js.
 * The file is read entirely in the browser — nothing is uploaded.
 * Throws PasswordRequiredError if the PDF requires a password.
 */
export async function loadPdf(file: File): Promise<PdfDocument> {
  const arrayBuffer = await file.arrayBuffer()
  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(arrayBuffer),
  })

  try {
    return await loadingTask.promise
  } catch (err) {
    throw normalizeLoadError(err)
  }
}

/**
 * Loads a password-protected PDF with the given password.
 * Throws WrongPasswordError if the password is incorrect.
 */
export async function loadPdfWithPassword(
  file: File,
  password: string,
): Promise<PdfDocument> {
  const arrayBuffer = await file.arrayBuffer()
  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(arrayBuffer),
    password,
  })

  try {
    return await loadingTask.promise
  } catch (err) {
    throw normalizeLoadError(err)
  }
}

function normalizeLoadError(err: unknown): Error {
  if (err instanceof Error) {
    const name = err.name
    const msg = err.message.toLowerCase()
    if (name === 'PasswordException') {
      if (msg.includes('incorrect') || msg.includes('wrong')) {
        return new WrongPasswordError()
      }
      return new PasswordRequiredError()
    }
    if (name === 'InvalidPDFException' || msg.includes('invalid')) {
      return new InvalidPdfError()
    }
  }
  return new InvalidPdfError()
}
